import { For, Show, Suspense, createSignal, createEffect } from "solid-js";
import { useSearchParams, A, createAsync, useAction, type RouteDefinition } from "@solidjs/router";
import { AppShell } from "~/components/layout";
import {
  Button, Badge, Card, CardContent, Skeleton, Input, ScrollArea, Avatar
} from "~/components/ui";
import { getConversations, getTopics, getDossiers, getChatMessages, sendChatMessage } from "~/server/bff";
import { formatRelativeTime } from "~/lib/utils";
import type { ChatMessage as ChatMessageType } from "~/lib/types";
import {
  Plus, Send, MessageSquare, Sparkles, ExternalLink, ChevronDown,
  X, Video, Tag, FolderOpen, Loader2
} from "lucide-solid";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: { videoId: string; videoTitle: string; snippet: string; timestamp: number }[];
}

function formatChatError(error: unknown): string {
  if (typeof error === "string") return error;
  if (!error || typeof error !== "object") {
    return "Sorry, an error occurred while processing your message. Please try again.";
  }
  const messages = Object.values(error as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0);

  return messages.length > 0
    ? messages.join(" ")
    : "Sorry, an error occurred while processing your message. Please try again.";
}

export const route = {
  preload: () => Promise.all([getConversations(), getTopics(), getDossiers()]),
} satisfies RouteDefinition;

export default function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Handle both string and string[] from searchParams
  const initialConversation = typeof searchParams.conversation === "string" 
    ? searchParams.conversation 
    : null;
  const [activeConversationId, setActiveConversationId] = createSignal<string | null>(initialConversation);
  const [messages, setMessages] = createSignal<Message[]>([]);
  const [inputValue, setInputValue] = createSignal("");
  const [scopeType, setScopeType] = createSignal<"all" | "topics" | "dossiers" | "videos">("all");
  const [selectedScopeIds, setSelectedScopeIds] = createSignal<string[]>([]);
  const [scopeOpen, setScopeOpen] = createSignal(false);
  const [isLoading, setIsLoading] = createSignal(false);

  const conversations = createAsync(() => getConversations(), { deferStream: true });
  const topics = createAsync(() => getTopics(), { deferStream: true });
  const dossiers = createAsync(() => getDossiers(), { deferStream: true });
  const sendChatAction = useAction(sendChatMessage);
  const conversationMessages = createAsync(
    () => {
      const id = activeConversationId();
      return id ? getChatMessages(id) : Promise.resolve([]);
    },
    { deferStream: true }
  );

  let messagesEndRef: HTMLDivElement | undefined;

  const scrollToBottom = () => {
    messagesEndRef?.scrollIntoView({ behavior: "smooth" });
  };

  createEffect(() => {
    if (messages().length > 0) {
      scrollToBottom();
    }
  });

  // Load persisted conversation history when switching conversations
  createEffect(() => {
    const history = conversationMessages();
    if (!history) return;

    const mapped = history.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      citations: msg.citations?.map((c) => ({
        videoId: c.videoId,
        videoTitle: c.videoTitle || "Unknown Video",
        snippet: c.snippet,
        timestamp: c.timestamp ?? 0,
      })),
    }));

    setMessages(mapped);
  });

  // Update URL when conversation changes
  createEffect(() => {
    const id = activeConversationId();
    if (id) {
      setSearchParams({ conversation: id });
    } else {
      setSearchParams({ conversation: undefined });
    }
  });

  const handleSend = async () => {
    if (!inputValue().trim() || isLoading()) return;

    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: inputValue(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const query = inputValue();
    setInputValue("");
    setIsLoading(true);

    try {
      const scopeSelection = {
        type: scopeType(),
        ids: selectedScopeIds(),
      };

      // Create FormData for the action
      const formData = new FormData();
      formData.set("message", query);
      formData.set("scopeType", scopeSelection.type);
      if (scopeSelection.type !== "all" && scopeSelection.ids.length > 0) {
        formData.set("scopeIds", scopeSelection.ids.join(","));
      }
      if (activeConversationId()) {
        formData.set("conversationId", activeConversationId()!);
      }

      const result = await sendChatAction(formData);

      if (result && "error" in result) {
        // Handle error
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: formatChatError(result.error),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } else if (result) {
        // Update conversation ID if new
        if (result.conversationId && !activeConversationId()) {
          setActiveConversationId(result.conversationId);
        }

        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: "assistant",
          content: result.answer,
          citations: result.citations?.map((c) => ({
            videoId: c.videoId,
            videoTitle: c.videoTitle || "Unknown Video",
            snippet: c.snippet,
            timestamp: c.timestamp || 0,
          })),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: error instanceof Error && error.message
          ? error.message
          : "Sorry, an error occurred while processing your message. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    setScopeType("all");
    setSelectedScopeIds([]);
  };

  return (
    <AppShell title="Chat">
      <h1 class="sr-only">Chat</h1>
      <div class="flex h-[calc(100vh-4rem)]">
        <div class="w-72 border-r flex flex-col bg-muted/30">
          <div class="p-4 border-b">
            <Button class="w-full" onClick={startNewConversation}>
              <Plus class="h-4 w-4 mr-2" />
              New Conversation
            </Button>
          </div>

          <ScrollArea class="flex-1">
            <div class="p-2">
              <Suspense fallback={<ConversationsSkeleton />}>
                <Show when={conversations()}>
                  {(convs) => (
                    <div class="space-y-1">
                      <For each={convs()}>
                        {(conv) => (
                          <button
                            class={`w-full text-left p-3 rounded-md transition-colors ${
                              activeConversationId() === conv.id
                                ? "bg-primary text-primary-foreground"
                                : "hover:bg-accent"
                            }`}
                            onClick={() => {
                              setActiveConversationId(conv.id);
                              setMessages([]);
                            }}
                          >
                            <p class="font-medium text-sm line-clamp-1">{conv.title}</p>
                            <p class={`text-xs mt-1 line-clamp-1 ${
                              activeConversationId() === conv.id
                                ? "text-primary-foreground/70"
                                : "text-muted-foreground"
                            }`}>
                              {conv.lastMessage}
                            </p>
                            <div class={`flex items-center gap-2 mt-2 text-xs ${
                              activeConversationId() === conv.id
                                ? "text-primary-foreground/70"
                                : "text-muted-foreground"
                            }`}>
                              <span>{formatRelativeTime(conv.updatedAt)}</span>
                              <Show when={conv.scope.type !== "all"}>
                                <Badge variant="outline" class="text-[10px] px-1">
                                  {conv.scope.type}
                                </Badge>
                              </Show>
                            </div>
                          </button>
                        )}
                      </For>
                    </div>
                  )}
                </Show>
              </Suspense>
            </div>
          </ScrollArea>
        </div>

        <div class="flex-1 flex flex-col">
          <div class="border-b px-4 py-3 flex items-center justify-between">
            <div class="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScopeOpen(!scopeOpen())}
              >
                <Show when={scopeType() === "all"} fallback={
                  <>
                    <Show when={scopeType() === "topics"}><Tag class="h-3 w-3 mr-1" /></Show>
                    <Show when={scopeType() === "dossiers"}><FolderOpen class="h-3 w-3 mr-1" /></Show>
                    <Show when={scopeType() === "videos"}><Video class="h-3 w-3 mr-1" /></Show>
                    {scopeType()}
                    <Show when={selectedScopeIds().length > 0}>
                      <span class="ml-1">({selectedScopeIds().length})</span>
                    </Show>
                  </>
                }>
                  <Sparkles class="h-3 w-3 mr-1" />
                  All content
                </Show>
                <ChevronDown class="h-3 w-3 ml-1" />
              </Button>

              <Show when={scopeOpen()}>
                <div class="absolute top-full left-0 mt-1 w-64 rounded-md border bg-popover p-1 shadow-md z-50">
                  <button
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-accent"
                    onClick={() => { setScopeType("all"); setSelectedScopeIds([]); setScopeOpen(false); }}
                  >
                    <Sparkles class="h-4 w-4" />
                    All content
                  </button>
                  
                  <div class="border-t my-1" />
                  
                  <p class="px-3 py-1 text-xs text-muted-foreground font-medium">Topics</p>
                  <Suspense fallback={<Skeleton class="h-8 mx-3" />}>
                    <Show when={topics()}>
                      {(t) => (
                        <For each={t()}>
                          {(topic) => (
                            <button
                              class={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-accent ${
                                scopeType() === "topics" && selectedScopeIds().includes(topic.id)
                                  ? "bg-accent"
                                  : ""
                              }`}
                              onClick={() => {
                                setScopeType("topics");
                                setSelectedScopeIds((prev) => 
                                  prev.includes(topic.id)
                                    ? prev.filter((id) => id !== topic.id)
                                    : [...prev, topic.id]
                                );
                              }}
                            >
                              <Tag class="h-4 w-4" />
                              {topic.name}
                              <Show when={selectedScopeIds().includes(topic.id)}>
                                <span class="ml-auto text-primary">✓</span>
                              </Show>
                            </button>
                          )}
                        </For>
                      )}
                    </Show>
                  </Suspense>

                  <div class="border-t my-1" />
                  
                  <p class="px-3 py-1 text-xs text-muted-foreground font-medium">Dossiers</p>
                  <Suspense fallback={<Skeleton class="h-8 mx-3" />}>
                    <Show when={dossiers()}>
                      {(d) => (
                        <For each={d()}>
                          {(dossier) => (
                            <button
                              class={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-accent ${
                                scopeType() === "dossiers" && selectedScopeIds().includes(dossier.id)
                                  ? "bg-accent"
                                  : ""
                              }`}
                              onClick={() => {
                                setScopeType("dossiers");
                                setSelectedScopeIds((prev) => 
                                  prev.includes(dossier.id)
                                    ? prev.filter((id) => id !== dossier.id)
                                    : [...prev, dossier.id]
                                );
                              }}
                            >
                              <FolderOpen class="h-4 w-4" />
                              {dossier.title}
                              <Show when={selectedScopeIds().includes(dossier.id)}>
                                <span class="ml-auto text-primary">✓</span>
                              </Show>
                            </button>
                          )}
                        </For>
                      )}
                    </Show>
                  </Suspense>

                  <Show when={scopeType() !== "all"}>
                    <div class="border-t my-1" />
                    <button
                      class="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded hover:bg-accent text-muted-foreground"
                      onClick={() => setScopeOpen(false)}
                    >
                      Done
                    </button>
                  </Show>
                </div>
              </Show>
            </div>
          </div>

          <ScrollArea class="flex-1 p-4">
            <div class="max-w-3xl mx-auto space-y-4">
              <Show
                when={messages().length > 0}
                fallback={
                  <div class="flex flex-col items-center justify-center h-[50vh] text-center">
                    <MessageSquare class="h-16 w-16 text-muted-foreground mb-4" />
                    <h2 class="text-xl font-semibold mb-2">Start a conversation</h2>
                    <p class="text-muted-foreground max-w-md">
                      Ask questions about your content. The AI will search across your videos
                      and provide answers with citations.
                    </p>
                    <div class="mt-6 flex flex-wrap gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInputValue("What are the main predictions made by creators this week?")}
                      >
                        Recent predictions
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInputValue("Summarize the key themes across my latest videos")}
                      >
                        Key themes
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInputValue("What claims need fact-checking?")}
                      >
                        Claims to verify
                      </Button>
                    </div>
                  </div>
                }
              >
                <For each={messages()}>
                  {(message) => (
                    <div
                      class={`flex gap-3 ${
                        message.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <Show when={message.role === "assistant"}>
                        <div class="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <Sparkles class="h-4 w-4 text-primary-foreground" />
                        </div>
                      </Show>

                      <div
                        class={`max-w-[80%] rounded-lg p-4 ${
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        }`}
                      >
                        <p class="text-sm whitespace-pre-wrap">{message.content}</p>

                        <Show when={message.citations && message.citations.length > 0}>
                          <div class="mt-3 pt-3 border-t border-border/50 space-y-2">
                            <p class="text-xs font-medium opacity-70">Sources:</p>
                            <For each={message.citations}>
                              {(citation) => (
                                <A
                                  href={`/video/${citation.videoId}?t=${citation.timestamp}`}
                                  class="block p-2 rounded bg-background/50 hover:bg-background transition-colors"
                                >
                                  <p class="text-xs font-medium flex items-center gap-1">
                                    {citation.videoTitle}
                                    <ExternalLink class="h-3 w-3" />
                                  </p>
                                  <p class="text-xs opacity-70 mt-1 italic line-clamp-2">
                                    "{citation.snippet}"
                                  </p>
                                </A>
                              )}
                            </For>
                          </div>
                        </Show>
                      </div>

                      <Show when={message.role === "user"}>
                        <Avatar size="sm" fallback="U" />
                      </Show>
                    </div>
                  )}
                </For>
                
                <Show when={isLoading()}>
                  <div class="flex gap-3 justify-start">
                    <div class="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <Sparkles class="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div class="bg-muted rounded-lg p-4">
                      <div class="flex items-center gap-2">
                        <Loader2 class="h-4 w-4 animate-spin" />
                        <span class="text-sm text-muted-foreground">Thinking...</span>
                      </div>
                    </div>
                  </div>
                </Show>
                
                <div ref={messagesEndRef} />
              </Show>
            </div>
          </ScrollArea>

          <div class="border-t p-4">
            <div class="max-w-3xl mx-auto flex gap-2">
              <Input
                placeholder="Ask about your content..."
                value={inputValue()}
                onInput={(e) => setInputValue(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                class="flex-1"
                disabled={isLoading()}
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue().trim() || isLoading()}
                aria-label="Send message"
                title="Send message"
              >
                <Show when={isLoading()} fallback={<Send class="h-4 w-4" />}>
                  <Loader2 class="h-4 w-4 animate-spin" />
                </Show>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function ConversationsSkeleton() {
  return (
    <div class="space-y-1">
      <For each={[1, 2, 3, 4]}>
        {() => (
          <div class="p-3">
            <Skeleton class="h-4 w-3/4 mb-2" />
            <Skeleton class="h-3 w-full" />
          </div>
        )}
      </For>
    </div>
  );
}
